import type { StagedEntity } from "@kuika/kuika-cl-model/src/dtos/branch.dto";
import {
  DATASOURCE_TYPE_DISPLAY_NAMES,
  STATEMENT_TYPE_DISPLAY_NAMES,
  DB_DATASOURCE_TYPES,
  CSHARP_DATASOURCE_TYPES,
  REST_DATASOURCE_TYPES,
  EDatasourceType
} from "@kuika/kuika-cl-model/src/enums/datasource-type";
import { EJsonEntityType } from "@kuika/kuika-cl-model/src/enums/entity-type";
import React, { useState } from "react";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import type { EntityDiffTone } from "./entity-diff-field";
import "./entity-diff.scss";

// ── Snapshot parsing ──

function parseSnapshot(snapshot: unknown): Record<string, unknown> | null {
  if (!snapshot) return null;
  if (typeof snapshot === "string") {
    try {
      return JSON.parse(snapshot) as Record<string, unknown>;
    } catch {
      return null;
    }
  }
  const obj = snapshot as Record<string, unknown>;
  const raw = obj.JSON ?? obj.Json ?? obj.json;
  if (raw && typeof raw === "string") {
    try {
      return JSON.parse(raw) as Record<string, unknown>;
    } catch {
      return null;
    }
  }
  if (raw && typeof raw === "object") return raw as Record<string, unknown>;
  return obj;
}

function parseJsonField(value: unknown): Record<string, unknown> | unknown[] | null {
  if (!value) return null;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  if (typeof value === "object") return value as Record<string, unknown>;
  return null;
}

function str(value: unknown): string {
  if (value == null) return "";
  return String(value);
}

function resolveDsTypeName(typeValue: unknown): string {
  if (typeValue == null) return "Unknown";
  const n = typeof typeValue === "number" ? typeValue : parseInt(String(typeValue), 10);
  return DATASOURCE_TYPE_DISPLAY_NAMES[n] ?? `Type ${n}`;
}

function maskConnectionString(cs: string): string {
  if (!cs) return "";
  return cs.replace(/(Password|Pwd)\s*=\s*[^;]+/gi, "$1=***");
}

// ── Summary field types ──

interface SummaryField {
  label: string;
  value: string;
  isCode?: boolean;
  language?: string;
}

interface EntitySummary {
  typeName: string;
  name: string;
  parentInfo?: string;
  fields: SummaryField[];
}

// ── Per-entity-type extraction ──

function extractActionSummary(data: Record<string, unknown>, allStaged?: StagedEntity[]): EntitySummary {
  const actionType = data.ActionType ?? data.actionType;
  const actionTypeNum = typeof actionType === "number" ? actionType : parseInt(str(actionType), 10);
  const typeName = resolveDsTypeName(actionType) + " Action";

  // Resolve parent datasource
  const dsId = str(data.KDatasource_Id ?? data.kDatasource_Id ?? "");
  let parentInfo: string | undefined;
  if (dsId && allStaged) {
    const parentDs = allStaged.find(
      (e) => String(e.entityType) === String(EJsonEntityType.KDatasource) && e.entityId === dsId
    );
    if (parentDs) {
      const parentData = parseSnapshot(parentDs.afterSnapshot ?? parentDs.beforeSnapshot);
      if (parentData) {
        parentInfo = `${str(parentData.Name ?? parentData.name)} (${resolveDsTypeName(parentData.DatasourceType ?? parentData.datasourceType)})`;
      }
    }
  }

  const fields: SummaryField[] = [];

  // Common fields
  const paramStr = str(data.Parameters ?? data.parameters ?? "");
  const params = parseJsonField(paramStr);
  const paramCount = Array.isArray(params) ? params.length : 0;

  if (DB_DATASOURCE_TYPES.has(actionTypeNum)) {
    if (data.TableName ?? data.tableName) fields.push({ label: "Table", value: str(data.TableName ?? data.tableName) });
    const stmtType = data.StatementType ?? data.statementType;
    if (stmtType != null) {
      const stmtTypeNum = typeof stmtType === "number" ? stmtType : parseInt(str(stmtType), 10);
      fields.push({ label: "Statement Type", value: STATEMENT_TYPE_DISPLAY_NAMES[stmtTypeNum] ?? str(stmtType) });
    }
    const stmt = str(data.Statement ?? data.statement ?? "");
    if (stmt) fields.push({ label: "Statement", value: stmt, isCode: true, language: "sql" });
  } else if (CSHARP_DATASOURCE_TYPES.has(actionTypeNum)) {
    const code = str(data.Code ?? data.code ?? "");
    if (code) fields.push({ label: "Code", value: code, isCode: true, language: "csharp" });
  } else if (REST_DATASOURCE_TYPES.has(actionTypeNum)) {
    if (paramCount > 0) {
      const paramNames = (params as Array<Record<string, unknown>>)
        .slice(0, 10)
        .map((p) => str(p.name ?? p.parameterName ?? p.Name ?? ""))
        .filter(Boolean);
      if (paramNames.length > 0) fields.push({ label: "Parameters", value: paramNames.join(", ") });
    }
  } else if (actionTypeNum === EDatasourceType.Blockly) {
    const blocklyInfo = parseJsonField(data.BlocklyActionInfo ?? data.blocklyActionInfo);
    if (blocklyInfo && !Array.isArray(blocklyInfo)) {
      const connectedAction = str((blocklyInfo as Record<string, unknown>).connectedActionName ?? "");
      if (connectedAction) fields.push({ label: "Connected Action", value: connectedAction });
    }
  } else if (actionTypeNum === EDatasourceType.AIActions) {
    const aiInfo = parseJsonField(data.AiActionInfo ?? data.aiActionInfo);
    if (aiInfo && !Array.isArray(aiInfo)) {
      const ai = aiInfo as Record<string, unknown>;
      if (ai.model) fields.push({ label: "Model", value: str(ai.model) });
      const prompt = str(ai.prompt ?? ai.systemPrompt ?? "");
      if (prompt)
        fields.push({ label: "Prompt", value: prompt.length > 200 ? prompt.substring(0, 200) + "..." : prompt });
    }
  } else if (actionTypeNum === EDatasourceType.AIAgent) {
    const agentInfo = parseJsonField(data.AiAgentInfo ?? data.aiAgentInfo);
    if (agentInfo && !Array.isArray(agentInfo)) {
      const agent = agentInfo as Record<string, unknown>;
      if (agent.name) fields.push({ label: "Agent", value: str(agent.name) });
    }
  } else if (actionTypeNum === EDatasourceType.SAP) {
    const sapInfo = parseJsonField(data.SapActionInfo ?? data.sapActionInfo);
    if (sapInfo && !Array.isArray(sapInfo)) {
      const sap = sapInfo as Record<string, unknown>;
      if (sap.functionModule ?? sap.FunctionModule) {
        fields.push({ label: "Function Module", value: str(sap.functionModule ?? sap.FunctionModule) });
      }
    }
  } else if (actionTypeNum === EDatasourceType.VisionAction) {
    const visionInfo = parseJsonField(data.VisionInfo ?? data.visionInfo);
    if (visionInfo && !Array.isArray(visionInfo)) {
      const vision = visionInfo as Record<string, unknown>;
      if (vision.description) fields.push({ label: "Description", value: str(vision.description) });
    }
  }

  // Return type info (for all action types)
  const returnType = str(data.ReturnTypeName ?? data.returnTypeName ?? "");
  if (returnType) {
    const cardinality = str(data.ReturnCardinality ?? data.returnCardinality ?? "");
    fields.push({ label: "Returns", value: returnType + (cardinality ? ` (${cardinality})` : "") });
  }

  if (paramCount > 0 && !REST_DATASOURCE_TYPES.has(actionTypeNum)) {
    fields.push({ label: "Parameters", value: `${paramCount} parameter(s)` });
  }

  return {
    typeName,
    name: str(data.Name ?? data.name ?? ""),
    parentInfo: parentInfo ? `Datasource: ${parentInfo}` : undefined,
    fields
  };
}

function extractDatasourceSummary(data: Record<string, unknown>, allStaged?: StagedEntity[]): EntitySummary {
  const dsType = data.DatasourceType ?? data.datasourceType;
  const dsTypeNum = typeof dsType === "number" ? dsType : parseInt(str(dsType), 10);
  const typeName = resolveDsTypeName(dsType);

  const fields: SummaryField[] = [];
  fields.push({ label: "Type", value: typeName });

  if (DB_DATASOURCE_TYPES.has(dsTypeNum)) {
    const cs = str(data.ConnectionString ?? data.connectionString ?? "");
    if (cs) fields.push({ label: "Connection", value: maskConnectionString(cs) });
  } else if (REST_DATASOURCE_TYPES.has(dsTypeNum)) {
    const restConn = parseJsonField(data.RestApiConnection ?? data.restApiConnection);
    if (restConn && !Array.isArray(restConn)) {
      const rc = restConn as Record<string, unknown>;
      if (rc.baseUrl ?? rc.BaseUrl) fields.push({ label: "Base URL", value: str(rc.baseUrl ?? rc.BaseUrl) });
    }
  } else if (dsTypeNum === EDatasourceType.OData) {
    const odataConn = parseJsonField(data.ODataConnection ?? data.odataConnection);
    if (odataConn && !Array.isArray(odataConn)) {
      const oc = odataConn as Record<string, unknown>;
      if (oc.serviceUrl ?? oc.ServiceUrl)
        fields.push({ label: "Service URL", value: str(oc.serviceUrl ?? oc.ServiceUrl) });
    }
  } else if (CSHARP_DATASOURCE_TYPES.has(dsTypeNum)) {
    const code = str(data.Code ?? data.code ?? "");
    if (code) fields.push({ label: "Code", value: code, isCode: true, language: "csharp" });
  } else if (dsTypeNum === EDatasourceType.Mqtt) {
    const mqttInfo = parseJsonField(data.MqttConnectionInfo ?? data.mqttConnectionInfo);
    if (mqttInfo && !Array.isArray(mqttInfo)) {
      const mq = mqttInfo as Record<string, unknown>;
      if (mq.brokerUrl ?? mq.BrokerUrl) fields.push({ label: "Broker", value: str(mq.brokerUrl ?? mq.BrokerUrl) });
    }
  }

  // Child actions count
  if (allStaged) {
    const dsId = str(data.Id ?? data.id ?? "");
    const childActions = allStaged.filter((e) => {
      if (String(e.entityType) !== String(EJsonEntityType.KAction)) return false;
      const actionData = parseSnapshot(e.afterSnapshot ?? e.beforeSnapshot);
      if (!actionData) return false;
      return str(actionData.KDatasource_Id ?? actionData.kDatasource_Id) === dsId;
    });
    if (childActions.length > 0) {
      const actionNames = childActions.slice(0, 5).map((a) => {
        const ad = parseSnapshot(a.afterSnapshot ?? a.beforeSnapshot);
        return ad ? str(ad.Name ?? ad.name ?? a.entityId) : a.entityId;
      });
      const suffix = childActions.length > 5 ? ` (+${childActions.length - 5} more)` : "";
      fields.push({ label: "Staged Actions", value: actionNames.join(", ") + suffix });
    }
  }

  return {
    typeName: "Datasource",
    name: str(data.Name ?? data.name ?? ""),
    fields
  };
}

function extractWorkflowSummary(data: Record<string, unknown>): EntitySummary {
  const fields: SummaryField[] = [];

  const displayName = str(data.DisplayName ?? data.displayName ?? "");
  if (displayName) fields.push({ label: "Display Name", value: displayName });

  const inactived = data.Inactived ?? data.inactived;
  if (inactived != null) {
    fields.push({ label: "Status", value: inactived ? "Inactive" : "Active" });
  }

  // Parse Json field for workflow structure
  const jsonField = data.Json ?? data.json;
  const wfModel = parseJsonField(jsonField);
  if (wfModel && !Array.isArray(wfModel)) {
    const wf = wfModel as Record<string, unknown>;
    if (wf.Description ?? wf.description) {
      fields.push({ label: "Description", value: str(wf.Description ?? wf.description) });
    }

    const phases = wf.HappyPathDefinition ?? wf.happyPathDefinition;
    if (Array.isArray(phases)) {
      fields.push({ label: "Phases", value: `${phases.length} phase(s)` });
    }

    const nodes = wf.Nodes ?? wf.nodes;
    if (nodes && typeof nodes === "object") {
      const n = nodes as Record<string, unknown>;
      const steps = Array.isArray(n.Steps ?? n.steps) ? ((n.Steps ?? n.steps) as unknown[]) : [];
      const decisions = Array.isArray(n.Decisions ?? n.decisions) ? ((n.Decisions ?? n.decisions) as unknown[]) : [];
      const gateways = Array.isArray(n.Gateways ?? n.gateways) ? ((n.Gateways ?? n.gateways) as unknown[]) : [];
      const events = Array.isArray(n.Events ?? n.events) ? ((n.Events ?? n.events) as unknown[]) : [];
      const parts: string[] = [];
      if (steps.length > 0) parts.push(`${steps.length} step(s)`);
      if (decisions.length > 0) parts.push(`${decisions.length} decision(s)`);
      if (gateways.length > 0) parts.push(`${gateways.length} gateway(s)`);
      if (events.length > 0) parts.push(`${events.length} event(s)`);
      if (parts.length > 0) fields.push({ label: "Nodes", value: parts.join(", ") });
    }

    const params = wf.Parameters ?? wf.parameters;
    if (Array.isArray(params) && params.length > 0) {
      fields.push({ label: "Parameters", value: `${params.length} parameter(s)` });
    }
  }

  return {
    typeName: "Workflow",
    name: str(data.Name ?? data.name ?? ""),
    fields
  };
}

function extractApiManagementSummary(data: Record<string, unknown>): EntitySummary {
  const fields: SummaryField[] = [];

  const jsonField = data.Json ?? data.json;
  const apiDef = parseJsonField(jsonField);
  if (apiDef && !Array.isArray(apiDef)) {
    const api = apiDef as Record<string, unknown>;
    // Try to find endpoint definitions
    const endpoints = api.endpoints ?? api.Endpoints ?? api.paths ?? api.Paths;
    if (Array.isArray(endpoints)) {
      fields.push({ label: "Endpoints", value: `${endpoints.length} endpoint(s)` });
      const listing = endpoints
        .slice(0, 5)
        .map((ep: Record<string, unknown>) => {
          const method = str(ep.method ?? ep.Method ?? ep.httpMethod ?? "").toUpperCase();
          const path = str(ep.path ?? ep.Path ?? ep.url ?? "");
          return method && path ? `${method} ${path}` : str(ep.name ?? ep.Name ?? "");
        })
        .filter(Boolean);
      if (listing.length > 0) {
        fields.push({ label: "Routes", value: listing.join("\n"), isCode: true });
      }
    }
  }

  return {
    typeName: "API Management",
    name: str(data.Name ?? data.name ?? ""),
    fields
  };
}

function extractScheduledJobSummary(data: Record<string, unknown>): EntitySummary {
  const fields: SummaryField[] = [];

  const jsonField = data.Json ?? data.json;
  const jobDef = parseJsonField(jsonField);
  if (jobDef && !Array.isArray(jobDef)) {
    const job = jobDef as Record<string, unknown>;
    const cron = str(job.cronExpression ?? job.CronExpression ?? job.schedule ?? job.Schedule ?? "");
    if (cron) fields.push({ label: "Schedule", value: cron });
    const target = str(job.actionName ?? job.ActionName ?? job.targetAction ?? job.TargetAction ?? "");
    if (target) fields.push({ label: "Target Action", value: target });
  }

  return {
    typeName: "Scheduled Job",
    name: str(data.Name ?? data.name ?? ""),
    fields
  };
}

function extractOfflineTableSummary(data: Record<string, unknown>): EntitySummary {
  const fields: SummaryField[] = [];

  const withData = data.WithData ?? data.withData;
  if (withData != null) fields.push({ label: "Include Data", value: withData ? "Yes" : "No" });

  const editing = data.EnableEditing ?? data.enableEditing;
  if (editing != null) fields.push({ label: "Editing Enabled", value: editing ? "Yes" : "No" });

  const where = str(data.WhereCriteria ?? data.whereCriteria ?? "");
  if (where) fields.push({ label: "Where Criteria", value: where, isCode: true, language: "sql" });

  const sendCriteria = str(data.WillSendRecordsCriteria ?? data.willSendRecordsCriteria ?? "");
  if (sendCriteria) fields.push({ label: "Send Records Criteria", value: sendCriteria, isCode: true, language: "sql" });

  return {
    typeName: "Offline Table",
    name: str(data.TableName ?? data.tableName ?? data.Name ?? data.name ?? ""),
    fields
  };
}

function extractProjectSummary(data: Record<string, unknown>): EntitySummary {
  const fields: SummaryField[] = [];

  const desc = str(data.Description ?? data.description ?? "");
  if (desc) fields.push({ label: "Description", value: desc });

  const schema = str(data.SchemaName ?? data.schemaName ?? "");
  if (schema) fields.push({ label: "Schema", value: schema });

  const isPrivate = data.IsPrivate ?? data.isPrivate;
  if (isPrivate != null) fields.push({ label: "Private", value: isPrivate ? "Yes" : "No" });

  return {
    typeName: "Project",
    name: str(data.Name ?? data.name ?? ""),
    fields
  };
}

function extractNotificationSummary(data: Record<string, unknown>): EntitySummary {
  const fields: SummaryField[] = [];

  const templateType = str(data.TemplateType ?? data.templateType ?? "");
  if (templateType) fields.push({ label: "Template Type", value: templateType });

  const multiLang = data.MultiLanguageEnabled ?? data.multiLanguageEnabled;
  if (multiLang != null) fields.push({ label: "Multi-Language", value: multiLang ? "Yes" : "No" });

  return {
    typeName: "Notification",
    name: str(data.Name ?? data.name ?? ""),
    fields
  };
}

// ── Main extraction dispatcher ──

function extractEntitySummary(entityType: number, snapshot: unknown, allStaged?: StagedEntity[]): EntitySummary | null {
  const data = parseSnapshot(snapshot);
  if (!data) return null;

  switch (entityType) {
    case EJsonEntityType.KAction:
      return extractActionSummary(data, allStaged);
    case EJsonEntityType.KDatasource:
      return extractDatasourceSummary(data, allStaged);
    case EJsonEntityType.KWorkFlow:
      return extractWorkflowSummary(data);
    case EJsonEntityType.KApiManagement:
      return extractApiManagementSummary(data);
    case EJsonEntityType.KScheduledJobs:
      return extractScheduledJobSummary(data);
    case EJsonEntityType.KOfflineTable:
      return extractOfflineTableSummary(data);
    case EJsonEntityType.KProject:
      return extractProjectSummary(data);
    case EJsonEntityType.KNotificationTemplate:
      return extractNotificationSummary(data);
    default:
      return {
        typeName: "Entity",
        name: str(data.Name ?? data.name ?? ""),
        fields: []
      };
  }
}

// ── Code block with expand/collapse ──

interface CodeBlockProps {
  value: string;
  language?: string;
}

function CodeBlock({ value, language }: CodeBlockProps): React.ReactElement {
  const [expanded, setExpanded] = useState(false);
  const MAX_LEN = 300;
  const needsTruncation = value.length > MAX_LEN;
  const displayValue = !expanded && needsTruncation ? value.substring(0, MAX_LEN) + "..." : value;

  return (
    <div>
      <pre className="kuika-entity-diff__code" data-expanded={expanded || undefined} data-language={language}>
        {displayValue}
      </pre>
      {needsTruncation && (
        <div className="kuika-entity-diff__show-more">
          <KuikaButton type="text" size="sm" onClick={() => setExpanded(!expanded)}>
            {expanded ? "Show less" : "Show more"}
          </KuikaButton>
        </div>
      )}
    </div>
  );
}

// ── Summary column renderer ──

interface SummaryColumnProps {
  summary: EntitySummary | null;
  label: string;
  otherSummary?: EntitySummary | null;
  highlightTone?: EntityDiffTone;
}

function SummaryColumn({ summary, label, otherSummary, highlightTone }: SummaryColumnProps): React.ReactElement {
  if (!summary) {
    return <div className="kuika-entity-diff__empty">{label}: No data</div>;
  }

  const isFieldChanged = (field: SummaryField): boolean => {
    if (!otherSummary) return false;
    const otherField = otherSummary.fields.find((f) => f.label === field.label);
    if (!otherField) return true;
    return otherField.value !== field.value;
  };

  return (
    <div className="kuika-entity-diff__summary-col">
      <div className="kuika-entity-diff__summary-col-head">{label}</div>
      <div className="kuika-entity-diff__summary-col-body">
        <div className="kuika-entity-diff__summary-name">{summary.name || "Unnamed"}</div>
        <div className="kuika-entity-diff__summary-type">{summary.typeName}</div>
        {summary.parentInfo && <div className="kuika-entity-diff__pill">{summary.parentInfo}</div>}
        {summary.fields.map((field, idx) => {
          const changed = isFieldChanged(field);
          return (
            <div
              key={idx}
              className="kuika-entity-diff__summary-field"
              data-changed={changed || undefined}
              data-tone={changed ? highlightTone : undefined}
            >
              <div className="kuika-entity-diff__summary-field-label">{field.label}</div>
              {field.isCode ? (
                <CodeBlock value={field.value} language={field.language} />
              ) : (
                <div className="kuika-entity-diff__value-text">{field.value}</div>
              )}
            </div>
          );
        })}
        {summary.fields.length === 0 && <div className="kuika-entity-diff__empty">No details available</div>}
      </div>
    </div>
  );
}

// ── Public component ──

export interface EntityContentSummaryProps {
  entityType: number;
  mode: "staged" | "merge";
  beforeSnapshot?: unknown;
  afterSnapshot?: unknown;
  baseSnapshot?: unknown;
  oursSnapshot?: unknown;
  theirsSnapshot?: unknown;
  allStagedEntities?: StagedEntity[];
}

export function EntityContentSummary({
  entityType,
  mode,
  beforeSnapshot,
  afterSnapshot,
  baseSnapshot,
  oursSnapshot,
  theirsSnapshot,
  allStagedEntities
}: EntityContentSummaryProps): React.ReactElement | null {
  if (entityType === EJsonEntityType.KScreenDesign) return null;

  if (mode === "staged") {
    const beforeSummary = extractEntitySummary(entityType, beforeSnapshot, allStagedEntities);
    const afterSummary = extractEntitySummary(entityType, afterSnapshot, allStagedEntities);

    if (!beforeSummary && !afterSummary) return null;

    return (
      <div className="kuika-entity-diff__summary-row">
        {beforeSummary !== null && afterSummary !== null ? (
          <>
            <SummaryColumn summary={beforeSummary} label="Before" otherSummary={afterSummary} highlightTone="removed" />
            <div className="kuika-entity-diff__summary-divider" />
            <SummaryColumn summary={afterSummary} label="After" otherSummary={beforeSummary} highlightTone="added" />
          </>
        ) : (
          <SummaryColumn summary={afterSummary ?? beforeSummary} label={afterSummary ? "Added" : "Removed"} />
        )}
      </div>
    );
  }

  // Merge mode
  const baseSummary = extractEntitySummary(entityType, baseSnapshot, allStagedEntities);
  const oursSummary = extractEntitySummary(entityType, oursSnapshot, allStagedEntities);
  const theirsSummary = extractEntitySummary(entityType, theirsSnapshot, allStagedEntities);

  if (!baseSummary && !oursSummary && !theirsSummary) return null;

  return (
    <div className="kuika-entity-diff__summary-row">
      <SummaryColumn summary={baseSummary} label="Base" />
      <div className="kuika-entity-diff__summary-divider" />
      <SummaryColumn summary={oursSummary} label="Target" otherSummary={theirsSummary} highlightTone="info" />
      <div className="kuika-entity-diff__summary-divider" />
      <SummaryColumn summary={theirsSummary} label="Source" otherSummary={oursSummary} highlightTone="warning" />
    </div>
  );
}

// ── Utility: detect entity type from merge conflict filePath ──

export function entityTypeFromFilePath(filePath: string): number | null {
  const map: Record<string, number> = {
    kaction: EJsonEntityType.KAction,
    kdatasource: EJsonEntityType.KDatasource,
    kproject: EJsonEntityType.KProject,
    kscreendesign: EJsonEntityType.KScreenDesign,
    kworkflow: EJsonEntityType.KWorkFlow,
    kapimanagement: EJsonEntityType.KApiManagement,
    knotificationtemplate: EJsonEntityType.KNotificationTemplate,
    kscheduledjobs: EJsonEntityType.KScheduledJobs,
    kofflinetable: EJsonEntityType.KOfflineTable,
    knotificationlanguagetemplate: EJsonEntityType.KNotificationLanguageTemplate
  };
  const lower = filePath.toLowerCase();
  for (const [key, val] of Object.entries(map)) {
    if (lower.includes(key)) return val;
  }
  return null;
}
